/**
 * Single source of truth for platform info (requirements, capabilities, connection methods).
 * Used by DynamicPlatformOnboarding, PlatformAuthForm, Onboarding home and the
 * admin Account Status page (via shared/components/PlatformInfo).
 *
 * Shape per platform:
 *   displayName, description
 *   requirements:      string[]  what the customer needs before connecting
 *   capabilities:      string[]  what Limitless can do once connected
 *   connectionMethods: { name, steps: string[] }[]  the different ways to connect
 *   onboardingSteps:   { title, details: string[] }[]  (optional) ordered steps to complete after
 *                      the connection methods above
 */

export const PLATFORM_CONFIG = {
  amazon: {
    displayName: 'Amazon Ads & Selling Partner',
    description: 'Connect Amazon Advertising (AD-API) and Selling Partner (SP-API) for ads and seller data.',
    requirements: [
      'Amazon Advertising account access',
      'Amazon Seller Central access (for SP-API)',
      'Entity ID (for SP-API onboarding)',
    ],
    capabilities: [
      'Campaign management and reporting (AD-API)',
      'Orders, inventory and sales data (SP-API)',
      'FBA data when fulfilled by Amazon',
    ],
    connectionMethods: [
      {
        name: 'Amazon Advertising (AD-API)',
        steps: ['Authorize via the "Onboarding Links" panel.'],
      },
      {
        name: 'Selling Partner (SP-API)',
        steps: [
          'Authorize via the SP-API onboarding link (requires an Entity ID).',
          'Enable FBA if the account fulfills via Amazon.',
        ],
      },
    ],
  },

  amc: {
    displayName: 'Amazon Marketing Cloud',
    description: 'Connect an Amazon Marketing Cloud (AMC) instance for clean-room analytics.',
    requirements: [
      'AMC Instance ID',
      'AMC Manager Account ID (if shared under a manager account)',
    ],
    capabilities: [
      'AMC queries and audiences',
      'Cross-channel analytics',
    ],
    connectionMethods: [
      {
        name: 'Provide instance details',
        steps: [
          'Provide the AMC Instance ID (and Manager Account ID if shared).',
          'Email help@limitless.tech so we can validate access.',
        ],
      },
    ],
  },

  dsp: {
    displayName: 'Amazon DSP',
    description: 'Connect an Amazon DSP seat to manage and report on programmatic campaigns.',
    requirements: [
      'Amazon DSP Account ID and Account Name',
      'Ability to add Limitless as an authorized advertiser/agency',
    ],
    capabilities: [
      'DSP campaign reporting',
      'Spend and performance tracking',
    ],
    connectionMethods: [
      {
        name: 'Agency access on DSP seat',
        steps: [
          'Provide the DSP Account ID and Account Name.',
          'Add Limitless as an authorized advertiser/agency on the DSP seat.',
        ],
      },
    ],
  },

  walmart: {
    displayName: 'Walmart Connect',
    description: 'Connect Walmart Connect advertising and, for sellers, the Marketplace API.',
    requirements: [
      'Admin access to the Walmart Connect advertising account',
      'Walmart Marketplace Admin user able to authorize access (sellers only)',
    ],
    capabilities: [
      'Walmart Connect campaign reporting and management (AD-API)',
      'Marketplace data for sellers (MP-API)',
      'Supplier data (SU-API, coming soon)',
    ],
    connectionMethods: [
      {
        name: 'Advertising Console user (AD-API access)',
        steps: [
          'Add admaster@limitless.tech in Advertising Console with Write access.',
          'See Walmart Connect guide: https://advertisinghelp.walmart.com/s/guides?article=000009334 ("Admin Management" > "Adding a user to your account").',
        ],
      },
      {
        name: 'API Partner (AD-API access)',
        steps: [
          'Log in to advertising.walmart.com > Account > Admin.',
          'In "API Partner - Advertiser level" click "Add Partner".',
          'Select "Limitless" with "Write Access" and click "Add Partner".',
        ],
      },
      {
        name: 'Supplier API keys (suppliers only, SU-API)',
        steps: ['Coming soon.'],
      },
    ],
    connectionNote: 'Only one of the Advertising Console user or API Partner methods is required.',
    onboardingSteps: [
      {
        title: 'AD-API onboarding (link to your Limitless account)',
        details: [
          'Typically completed internally by Limitless once access above is granted.',
          'To speed it up, provide your Advertiser ID and, if you are a seller, your Seller ID.',
          'Submit them at /customer/onboarding/walmart/ad-api.',
        ],
      },
      {
        title: 'MP-API onboarding (Marketplace, sellers only)',
        details: [
          'Available after AD-API onboarding is complete.',
          'This is an OAuth flow. A Walmart Marketplace Admin user must sign in and grant Limitless permission.',
          'Start at /customer/onboarding/walmart/mp-api.',
        ],
      },
    ],
  },

  tiktok: {
    displayName: 'TikTok Ads',
    description:
      'Connect your TikTok Ads account to sync campaigns and performance data.',
    requirements: [
      'TikTok For Business login',
      'Access to the TikTok ad account',
      'Permission to authorize account access',
    ],
    capabilities: [
      'Campaign performance reporting',
      'Ad spend and metrics tracking',
      'Creative performance insights',
      'Audience analytics',
    ],
    connectionMethods: [
      {
        name: 'Provide account IDs',
        steps: [
          'Provide the TikTok Advertiser ID and Business Center ID.',
          'Provide the Catalog ID, Pixel ID and Product Set ID if running catalog/conversion campaigns.',
        ],
      },
    ],
  },

  google: {
    displayName: 'Google Ads',
    description:
      'Connect your Google Ads account to sync campaigns, keywords, and performance data.',
    requirements: [
      'Google account',
      'Access to the Google Ads account',
      'Permission to manage the requested advertising data',
    ],
    capabilities: [
      'Campaign and ad group reporting',
      'Keyword performance tracking',
      'Conversion tracking',
      'Search query insights',
      'Budget and spend analytics',
      'Shopping and Performance Max feeds (with Merchant Center)',
    ],
    connectionMethods: [
      {
        name: 'Direct to the customer ad account',
        steps: [
          'Customer runs through the Google onboarding flow to grant permission.',
          'Customer identifies the Google Customer ID to use (there can be multiple).',
          'Merchant Center ID is needed for Shopping/Performance Max feeds and if configured can be linked form the onboard page.',
        ],
      },
      {
        name: 'Limitless as Manager (MCC)',
        steps: [
          'Assign Limitless AdMaster (524-067-9842) as a Manager in the Google Ads account.',
          'Customer provides the Google Customer ID, or we pick it from the returned list.',
          'Once assigned, use the onboarding URL to get a Google Refresh Token.',
          'Merchant Center ID is needed for Shopping/Performance Max feeds and if configured can be linked form the onboard page.',
        ],
      },
    ],
  },

  meta: {
    displayName: 'Meta Advertising',
    description:
      'Connect your Meta ad account to sync Facebook and Instagram campaigns and performance data.',
    requirements: [
      'Facebook account',
      'Access to the Meta ad account',
      'Permission to manage campaigns or view performance data',
    ],
    capabilities: [
      'Campaign performance reporting',
      'Ad creative insights',
      'Audience targeting analytics',
      'Conversion tracking',
      'Facebook and Instagram reporting',
    ],
    connectionMethods: [
      {
        name: 'BISU (Business Integration System User) OAuth',
        steps: [
          'Start the BISU OAuth onboarding process.',
          'Connect: Business Portfolio, Facebook Page, Instagram Account, Pixel and Ad Account.',
        ],
      },
    ],
  },

  microsoft: {
    displayName: 'Microsoft Advertising',
    description:
      'Connect your Microsoft Advertising account to sync campaigns and performance data.',
    requirements: [
      'Microsoft account',
      'Access to the Microsoft Advertising account',
      'Permission to manage the requested advertising data',
    ],
    capabilities: [
      'Campaign and ad group reporting',
      'Keyword performance tracking',
      'Search advertising analytics',
      'Conversion tracking',
      'Budget and spend insights',
    ],
    connectionMethods: [
      {
        name: 'Azure AD app registration',
        steps: [
          'Provide the Microsoft Advertising Account ID.',
          'Provide the Client ID, Object ID and Tenant ID from the Azure AD app registration used for API access.',
        ],
      },
    ],
  },

  'criteo-target': {
    displayName: 'Target Roundel (via Criteo)',
    description:
      'Authorize Limitless through Criteo to create, manage, and report on Target Roundel campaigns.',
    requirements: [
      'Criteo account linked to your Target advertiser portfolio',
      'Access to the Target portfolio in Criteo',
      'Admin, Business Manager, or Technical Manager role',
    ],
    capabilities: [
      'Campaign creation and management',
      'Campaign performance reporting',
      'Ad spend and metrics tracking',
      'ROAS and conversion tracking',
      'Online and in-store sales reporting',
    ],
    connectionMethods: [
      {
        name: 'Criteo authorization',
        steps: [
          'Authorize Limitless through Criteo for the Target portfolio.',
          'Provide the Target Vendor ID (and RMS Account ID / Supplier ID depending on partnership type).',
          'Confirm the account currency and time zone for reporting accuracy.',
        ],
      },
    ],
  },
};

// Keys used elsewhere in the app that map to a differently named config entry
const PLATFORM_KEY_ALIASES = {
  target: 'criteo-target',
};

/**
 * Get platform configuration by key
 * @param {string} adPlatform - Platform key (e.g., 'tiktok', 'google')
 * @returns {object|null} Platform config or null if not found
 */
export const getPlatformConfig = (adPlatform) => {
  if (!adPlatform) return null;
  const key = adPlatform.toLowerCase();
  return PLATFORM_CONFIG[PLATFORM_KEY_ALIASES[key] || key] || null;
};
